import SEO from "../components/seo.jsx"
import { HT } from "../siteConfig";
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronDown } from "lucide-react";
import HeroImg from "../assets/bodyHt-hero.png";
import BreadcrumbImg from "../assets/bodyHt-breadcrumb-bg.jpg";
import SurgeonImg from "../assets/drbhaveshgupta.jpeg";
import ProcedureImg from "../assets/bodyHt-procedure-closeup.jpg";
import TypesBH from "../assets/body-hair.png"

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

/* ============================================================
   CONTENT pulled straight from the PHP "Body Hair Transplant" page
============================================================ */
const PAGE = {

  hero: {
    eyebrow: "Advanced Hair Restoration",
    heading: "Body Hair Transplant",
    sub: "Expanding what's possible in hair restoration natural, lasting results by Dr. Bhavesh Gupta at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Body Hair Transplant", href: "/body-hairs-transplants.php" },
  ],

  intro: {
    eyebrow: "Overview",
    heading: `Body Hair Transplant in ${HT.placeName}`,
    paragraphs: [
      "Body Hair Transplant (BHT) is a cutting-edge procedure that involves transferring hair follicles from different areas of the body, like the chest, back, or beard, to areas with thinning or balding hair, such as the scalp. This technique offers a solution for individuals with limited donor hair on the scalp, expanding the possibilities of hair restoration.",
      "BHT ensures natural-looking results and has gained traction as an effective option for those seeking hair regrowth and a fuller, more vibrant appearance.",
      `Body Hair Transplant in ${HT.placeName} at Clinique Internationale procedures are expertly performed, offering a reliable solution for hair restoration. Their skilled team utilizes advanced techniques to harvest and transplant body hair to areas affected by hair loss, ensuring natural-looking results with a focus on patient comfort and safety.`,
    ],
  },

  surgeon: {
    eyebrow: "Lead Surgeon",
    heading: `Best Body Hair Transplant Surgeon in ${HT.placeName}`,
    paragraphs: [
      `Dr. Bhavesh Gupta is a specialist in body hair transplant in ${HT.placeName} and performs intricate procedures transferring hair follicles from areas like the chest, back, or other body parts to regions with hair loss, often the scalp.`,
      "Utilizing advanced techniques, he meticulously extracts and implants individual follicular units to ensure natural-looking and long-lasting results, with expertise in both donor site extraction and recipient area implantation.",
      `Dr. Gupta's expertise in body hair transplant in ${HT.placeName} at Clinique Internationale has helped numerous individuals achieve natural-looking results and regain their confidence, with a reputation for precision and dedicated patient care.`,
    ],
  },

  cost: {
    eyebrow: "Pricing",
    heading: "Body Hair Transplant Cost",
    intro:
      `The cost of a body hair transplant in ${HT.placeName} typically ranges from ₹50,000 to ₹2,00,000, depending on factors like the number of grafts required, the clinic's reputation, and the expertise of the surgeon. The overall expense encompasses consultation fees, procedure charges, medications, and follow-up care.`,
    outro: "It's advisable to consult with the clinic directly for an accurate quote tailored to your individual needs and expectations.",
    cities: [
      { city: "Pune", range: "₹40,000 – ₹1,50,000", href: "https://pune.hairclinique.in/" },
      { city: "Bangalore", range: "₹70,000 – ₹2,50,000", href: "https://bangalore.hairclinique.in/" },
      { city: "Mumbai", range: "₹80,000 – ₹2,00,000", href: "/" },
      { city: "Shirdi", range: "₹35,000 – ₹1,50,000", href: "https://shirdi.hairclinique.in/" },
    ],
  },

  types: {
    eyebrow: "The Options",
    heading: "Types of Body Hair Transplant",
    intro: "There are a few types of body hair transplant procedures that are commonly performed.",
    items: [
      { num: "01", title: "Beard Hair Transplant", desc: "Transplanting hair from the back of the scalp or chest to the beard area commonly sought by individuals looking to enhance or fill in their beards." },
      { num: "02", title: "Eyebrow Hair Transplant", desc: "For those seeking fuller or reshaped eyebrows, hair is taken from other parts of the body and transplanted to the brow region." },
      { num: "03", title: "Chest Hair Transplant", desc: "Some individuals opt for chest hair transplantation to enhance density or fill in areas with minimal growth." },
      { num: "04", title: "Pubic Hair Transplant", desc: "A less common procedure, occasionally requested by individuals seeking to increase the density of pubic hair." },
    ],
  },

  faqs: {
    eyebrow: "Common Questions",
    heading: "Body Hair Transplant FAQ",
    items: [
      {
        q: "What is a body hair transplant?",
        a: "At Clinique Internationale, Mumbai, a body hair transplant is a cosmetic procedure where hair follicles are taken from one part of the body, such as the chest, back, or legs, and transplanted to areas requiring hair restoration, like the scalp, beard, or eyebrows.",
      },
      {
        q: "Who is a good candidate for a body hair transplant?",
        a: "Ideal candidates at Clinique Internationale are individuals with insufficient donor hair on the scalp for a conventional transplant, those with patchy hair growth, or anyone looking to restore hair in areas with thinning or scarring.",
      },
      {
        q: "Is the procedure painful?",
        a: "The procedure at Clinique Internationale is performed under local anesthesia, ensuring minimal discomfort. Mild soreness, redness, or swelling may occur for a few days but usually subsides quickly.",
      },
      {
        q: "Are the results permanent?",
        a: "Yes. Hair follicles transplanted from the body at Clinique Internationale are permanent and continue to grow naturally in their new location.",
      },
      {
        q: "How many grafts can be taken from body hair?",
        a: "The number of grafts depends on the donor area and recipient site. At Clinique Internationale, chest, back, or beard hair can typically provide sufficient follicles, and a consultation is conducted to plan the transplant accurately.",
      },
    ],
  },

  cta: {
    eyebrow: "Take the First Step",
    heading: "Ready to Explore Body Hair Transplant?",
    sub: "Book a personalised consultation with Dr. Bhavesh Gupta at Clinique Internationale and take the first step toward natural, lasting results.",
    primary: { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration" },
    secondary: { label: "View All Treatments", href: "/body-hairs-transplants.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${
        align === "center" ? "justify-center" : "justify-start"
      } ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 ${
        align === "center" ? "text-center" : "text-left"
      } ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

function FaqItem({ faq, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;
  return (
    <div className="border border-[#c9a84c]/15 rounded-xl overflow-hidden bg-white/[0.03]">
      <button
        onClick={() => setOpenIndex(isOpen ? null : index)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#c9a84c]/[0.06] transition-colors"
      >
        <span style={cinzel} className="text-[12.5px] sm:text-[13px] font-bold tracking-[0.02em] text-white/90">
          {String(index + 1).padStart(2, "0")}. {faq.q}
        </span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-[#c9a84c] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-4 text-[13px] leading-[1.8] text-white/55">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function BodyHairTransplant({ url = "https://www.hairclinique.in/body-hairs-transplants.php" }) {
  const pageRef = useRef(null);
  const heroEyeRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRefs = useRef([]);
  const addSection = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  /* full-bleed wrapper: fills the viewport with generous edge padding instead of a centered max-width column */
  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title={`Body Hair Transplant in ${HT.placeName} | Clinique Internationale`}
  description={`Advanced body hair transplant (BHT) procedures in ${HT.placeName} at Clinique Internationale, led by Dr. Bhavesh Gupta. Restore density with natural, lasting results.`}
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

<div
  ref={pageRef}
  className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20"
  style={{ fontFamily: "'Raleway', sans-serif" }}
>
  {/* ══════════════════════════════════════
      HERO full width
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0" />
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
      }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-[#c9a84c] mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
        {PAGE.hero.eyebrow}
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
      </p>
      <h1
        ref={heroHeadRef}
        style={cinzel}
        className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-2 sm:mb-3 leading-[1.15] sm:leading-tight"
      >
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-white/70 leading-relaxed ml-auto">
        {PAGE.hero.sub}
      </p>
    </div>
  </div>


        {/* ── BREADCRUMB full width with background image ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href ? (
                  <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
                    {b.label}
                  </a>
                ) : (
                  <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
                    {b.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={ProcedureImg} alt="Hair transplant procedure close-up" className="h-[260px] sm:h-[320px] md:h-[380px] order-last md:order-first" />
            <div>
              <Eyebrow>{PAGE.intro.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.intro.heading}</SectionHeading>
              {PAGE.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            SURGEON (dark band, text + image)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <Eyebrow light>{PAGE.surgeon.eyebrow}</Eyebrow>
                <SectionHeading light>{PAGE.surgeon.heading}</SectionHeading>
                {PAGE.surgeon.paragraphs.map((p, i) => (
                  <p key={i} className="text-[13.5px] leading-[1.9] text-white/60 mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <img src={SurgeonImg} alt="Dr. Bhavesh Gupta, body hair transplant surgeon" className="h-[260px] sm:h-[320px] md:h-[460px] rounded-2xl" />
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            COST BY CITY
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.cost.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto mb-8">
              {PAGE.cost.cities.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group"
                >
                  <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-3 group-hover:text-[#a07828] transition-colors">
                    {c.city}
                  </div>
                  <div
                    style={cinzel}
                    className="font-black text-[1.05rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent"
                  >
                    {c.range}
                  </div>
                </a>
              ))}
            </div>
            <p className="text-[13px] leading-[1.85] text-[#6b5535] italic max-w-3xl mx-auto text-center">{PAGE.cost.outro}</p>
          </div>
        </div>

        {/* ══════════════════════════════════════
            TYPES (image + cards)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.types.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.types.heading}</SectionHeading>
              <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.types.intro}</p>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
                <FramedImage src={TypesBH} alt="Body hair transplant procedure close-up" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {PAGE.types.items.map((t) => (
                    <div key={t.num} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-shadow">
                      <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                      <span style={cinzel} className="font-black text-[1.6rem] leading-none text-[#c9a84c]/20 select-none block mb-3">
                        {t.num}
                      </span>
                      <div style={cinzel} className="text-[12.5px] font-bold tracking-[0.05em] uppercase text-[#1a0f02] mb-2">
                        {t.title}
                      </div>
                      <p className="text-[13px] leading-[1.8] text-[#6b5535]">{t.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            FAQ (dark band accordion)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="max-w-3xl mx-auto">
              <Eyebrow light align="center">{PAGE.faqs.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.faqs.heading}</SectionHeading>
              <div className="flex flex-col gap-3">
                {PAGE.faqs.items.map((faq, i) => (
                  <FaqItem key={i} faq={faq} index={i} openIndex={openFaq} setOpenIndex={setOpenFaq} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5"
            >
              {PAGE.cta.heading}
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PAGE.cta.primary.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                {PAGE.cta.primary.label}
              </a>
              <a
                href={PAGE.cta.secondary.href}
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}